import {
	HTTPFetchResponse,
	GenericRequest,
	PrimaryTokenStatus,
} from './dsAuth/data/AuthTypes';
import { AuthChallenge } from './dsAuth/data/AuthChallenge';
import { HeaderParser } from './dsAuth/parser/HeaderParser';
import { Logger } from './Logger';
import { primaryTokenStatusConstants } from './AuthConstants';
import { AotCategory, logConstants } from '../utils/Constants';
import TokenManager, { PrimaryTokensUpdateReason } from './TokenManager';
import { PrimaryTokenFetchFailure } from './AuthErrors';
import {
	RequestTokenResponse,
	RequestTokenResponseParser,
} from './dsAuth/parser/RequestTokenResponseParser';
import AuthUtils from './AuthUtils';
import { SentryLogger } from '../sentryLogging/SentryLogger';
import { AuthBridgeFactory } from '../AccountConfig/bridge/auth/AuthBridgeFactory';

const authBridge = AuthBridgeFactory.getBridge();

export abstract class PrimaryTokenServiceBase {
	//TODO-LLT: Fallback: no need to getAuthChallengeFromResponse in case of fallback since oidc would have already done it
	protected async getAuthChallengeFromResponse(
		response: HTTPFetchResponse,
		id: string,
		transactionId: string,
		referenceUri: string
	): Promise<AuthChallenge> {
		const authChallengeFromResponse = HeaderParser.getInstance().parseAuthHeader(
			response.headers
		);

		await AuthUtils.getInstance().takeActionForAuthChallenge(
			authChallengeFromResponse,
			referenceUri,
			id,
			transactionId,
			true
		);
		return authChallengeFromResponse;
	}

	protected async fetchToken(
		tokenRequest: GenericRequest,
		referenceUri: string,
		id: string
	): Promise<HTTPFetchResponse> {
		const { method, headers, body, url } = tokenRequest;
		try {
			return await authBridge.authFetch(method, url, headers, body, referenceUri, true);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: PRIMARY_TOKEN_REQUEST_FAILURE`
			);
			throw new PrimaryTokenFetchFailure(e);
		}
	}

	protected async validateAndStoreToken(
		tokenResponse: HTTPFetchResponse,
		referenceUri: string,
		id: string,
		reason: PrimaryTokensUpdateReason,
		storeAuthDomain = true
	): Promise<PrimaryTokenStatus> {
		try {
			const requestTokenResponse = RequestTokenResponseParser.getInstance().parseXML(
				tokenResponse.body
			) as RequestTokenResponse;
			Logger.getInstance().write(
				logConstants.INFO,
				id + ': Primary token received',
				AotCategory.Info,
				`Primary token received`
			);

			await TokenManager.getInstance().storePrimaryToken(
				requestTokenResponse,
				referenceUri,
				reason,
				storeAuthDomain
			);
			return new PrimaryTokenStatus(
				primaryTokenStatusConstants.PRIMARY_TOKEN_STORED,
				true
			);
		} catch (e) {
			SentryLogger.getInstance().logError(e, { tags: { url: referenceUri } });
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ':VALIDATE_AND_STORE_TOKEN_FAIL'
			);
			throw new PrimaryTokenFetchFailure(e);
		}
	}
}
